import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { fileService } from "@/services/file-service";
import { getMapPreviewSource } from "@/components/pages/general-info/utils/hotelMapPreview";

interface HotelMapPreviewProps {
  selectedFile?: File;
  savedFileId?: string | null;
  onUploadAnother: () => void;
}

export const HotelMapPreview = ({
  selectedFile,
  savedFileId,
  onUploadAnother,
}: HotelMapPreviewProps): React.JSX.Element | null => {
  const { t } = useTranslation();
  const [selectedUrl, setSelectedUrl] = useState<string>();
  const [savedUrl, setSavedUrl] = useState<string>();
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (!selectedFile) {
      setSelectedUrl(undefined);
      return;
    }
    const url = URL.createObjectURL(selectedFile);
    setSelectedUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [selectedFile]);

  useEffect(() => {
    let cancelled = false;
    setSavedUrl(undefined);
    setHasError(false);
    if (!savedFileId) return;
    fileService
      .getSignedUrl(savedFileId)
      .then((url) => {
        if (!cancelled) setSavedUrl(url);
      })
      .catch(() => {
        if (!cancelled) setHasError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [savedFileId]);

  const source = getMapPreviewSource({ selectedUrl, savedUrl });

  if (hasError && source.kind !== "selected") {
    return (
      <div
        role="alert"
        className="flex flex-col items-center gap-2 py-4 text-center text-sm"
      >
        <span className="text-destructive">
          {t("hotels.generalInfo.mapPreviewError")}
        </span>
        <button
          type="button"
          className="text-primary underline"
          onClick={onUploadAnother}
        >
          {t("hotels.generalInfo.uploadAnotherFile")}
        </button>
      </div>
    );
  }

  if (source.kind === "none") return null;

  return (
    <object
      key={source.url}
      data={source.url}
      type="application/pdf"
      aria-label={t("hotels.generalInfo.hotelMap")}
      className="bg-background h-96 w-full rounded-lg border"
    >
      <span className="text-muted-foreground text-sm">
        {t("hotels.generalInfo.mapPreviewError")}
      </span>
    </object>
  );
};
